'use client';

import { useMemo, useState } from 'react';

import { CalendarIcon, X } from 'lucide-react';

import { Button } from '@kit/ui/button';
import { Calendar } from '@kit/ui/calendar';
import { Input } from '@kit/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@kit/ui/popover';
import { cn } from '@kit/ui/utils';

import {
  dateToIso,
  displayToIso,
  getDateOrder,
  getDatePlaceholder,
  isoToDate,
  isoToDisplay,
} from '~/lib/date-format';

export interface DateFieldProps {
  id?: string;
  /** ISO `yyyy-mm-dd`, or `''` for empty, the same shape `type="date"` used. */
  value: string;
  onChange: (value: string) => void;
  /**
   * The currency the surrounding record is denominated in. It decides whether
   * the date reads day-first or month-first; omit it and the field reads
   * day-first, which is the convention nearly everywhere.
   */
  currency?: string | null;
  disabled?: boolean;
  /**
   * Show a clear control so an optional date can be set back to empty.
   * Empty is a real value: the date is unknown, not "today".
   */
  clearable?: boolean;
  min?: string;
  max?: string;
  className?: string;
  'aria-label'?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  'data-test'?: string;
}

/**
 * A date input whose written format the page controls.
 *
 * Text and calendar are the same field: the box is typed into directly, and
 * the button beside it opens a month grid for anyone who would rather point at
 * it. The button sits inside the input's own padding rather than against its
 * border, which is the spacing complaint against the native control, whose
 * calendar glyph is jammed into the right edge with no room around it.
 *
 * See `~/lib/date-format` for why the format cannot be left to the browser.
 */
export function DateField({
  id,
  value,
  onChange,
  currency,
  disabled,
  clearable = false,
  min,
  max,
  className,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  'data-test': dataTest,
}: DateFieldProps) {
  const order = useMemo(() => getDateOrder(currency), [currency]);
  const [text, setText] = useState(() => isoToDisplay(value, order));
  const [synced, setSynced] = useState({ order, value });
  const [open, setOpen] = useState(false);

  /*
   * The field is controlled, but only from outside. Re-deriving the text on
   * every render would rewrite what somebody is halfway through typing (`3/9`
   * is not yet a date, so `value` is still the old one), so the text is
   * re-derived only when the props genuinely change under it — a reset, a
   * record loading in, or the currency flipping the field order.
   *
   * Adjusted during render rather than in an effect: React re-runs this
   * component immediately with the new state and paints once, where an effect
   * would show the stale text first and then correct it.
   */
  if (synced.value !== value || synced.order !== order) {
    setSynced({ order, value });

    if (displayToIso(text, order) !== (value || null)) {
      setText(isoToDisplay(value, order));
    }
  }

  const selected = isoToDate(value);
  const hasValue = Boolean(value);
  const showClear = clearable && hasValue && !disabled;

  function commit(next: string) {
    setText(next);

    if (next.trim() === '') {
      onChange('');

      return;
    }

    const iso = displayToIso(next, order);

    if (iso) onChange(iso);
  }

  function clear() {
    setText('');
    onChange('');
    setOpen(false);
  }

  return (
    <div className={cn('relative', className)}>
      <Input
        id={id}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        data-test={dataTest}
        disabled={disabled}
        inputMode="numeric"
        placeholder={getDatePlaceholder(order)}
        value={text}
        onChange={(event) => commit(event.target.value)}
        onBlur={() => setText(isoToDisplay(value, order))}
        /* Room for the calendar trigger, and the clear control when shown. */
        className={showClear ? 'pr-[4.25rem]' : 'pr-11'}
      />

      {showClear ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Clear date"
          className="text-muted-foreground hover:text-foreground absolute top-1/2 right-9 h-7 w-7 -translate-y-1/2"
          onClick={clear}
        >
          <X className="h-4 w-4" />
        </Button>
      ) : null}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled}
            aria-label="Open calendar"
            className="text-muted-foreground hover:text-foreground absolute top-1/2 right-1.5 h-7 w-7 -translate-y-1/2"
          >
            <CalendarIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>

        <PopoverContent align="end" className="w-auto p-0">
          <Calendar
            mode="single"
            autoFocus
            captionLayout="dropdown"
            defaultMonth={selected}
            selected={selected}
            disabled={[
              ...(min ? [{ before: isoToDate(min) as Date }] : []),
              ...(max ? [{ after: isoToDate(max) as Date }] : []),
            ]}
            onSelect={(date) => {
              onChange(date ? dateToIso(date) : '');
              setOpen(false);
            }}
          />
          {clearable ? (
            <div className="border-border border-t p-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-full"
                disabled={!hasValue}
                onClick={clear}
              >
                Clear
              </Button>
            </div>
          ) : null}
        </PopoverContent>
      </Popover>
    </div>
  );
}
